import { useCallback, useEffect, useState } from 'react';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

export type RsvpResponsesVersion = 'classic' | 'beta';

// localStorage key — global to the admin user (not scoped per event), matching useColorScheme: one
// person opens many events back-to-back and we want their chosen view to stick across all of them.
const STORAGE_KEY = 'rsvp:answers:version';

// Classic is the default for the 100% rollout: every admin lands on the familiar page first and is
// prompted to opt into the redesign. Their choice then persists here.
const DEFAULT_VERSION: RsvpResponsesVersion = 'classic';

const isValidVersion = (value: string | null): value is RsvpResponsesVersion => value === 'classic' || value === 'beta';

const readStoredVersion = (): RsvpResponsesVersion => {
  if (typeof window === 'undefined') return DEFAULT_VERSION;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isValidVersion(stored) ? stored : DEFAULT_VERSION;
  } catch {
    return DEFAULT_VERSION;
  }
};

// Module-level store so every mounted consumer of this hook shares one version value. Per-component
// `useState(readStoredVersion)` gave each caller its own snapshot: opting into beta on the Answers
// page updated that instance + localStorage but left sibling instances (e.g. RsvpTabNav's "New"
// badge) reading the stale value until they remounted. A single external store fixes that — every
// `useSyncExternalStore` subscriber re-renders on change, and the native `storage` event keeps other
// tabs in sync too (it only fires cross-tab, so same-tab sync rides on `notify` directly).
let currentVersion: RsvpResponsesVersion | null = null;
const listeners = new Set<() => void>();

const getSnapshot = (): RsvpResponsesVersion => {
  if (currentVersion === null) currentVersion = readStoredVersion();
  return currentVersion;
};

const notify = () => listeners.forEach(listener => listener());

const setVersionGlobal = (next: RsvpResponsesVersion) => {
  currentVersion = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // swallow — Safari private mode / quota; the in-memory store still drives this session.
  }
  notify();
};

// Cross-tab: a `storage` event fires in *other* tabs when localStorage changes here. Re-read and
// notify so a second open tab's view + badge follow along. Same-tab updates never raise this event;
// those go through `notify` in `setVersionGlobal` instead.
const handleStorage = (event: StorageEvent) => {
  if (event.key !== STORAGE_KEY) return;
  const next = isValidVersion(event.newValue) ? event.newValue : DEFAULT_VERSION;
  if (next === currentVersion) return;
  currentVersion = next;
  notify();
};

const subscribe = (listener: () => void): (() => void) => {
  if (listeners.size === 0 && typeof window !== 'undefined') {
    window.addEventListener('storage', handleStorage);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener('storage', handleStorage);
    }
  };
};

type UseRsvpResponsesVersionReturn = Readonly<{
  // The version to render right now. Forced to 'classic' whenever the beta is unavailable (kill
  // switch off), so callers never need to special-case the flag when picking a page to render.
  version: RsvpResponsesVersion;
  setVersion: (version: RsvpResponsesVersion) => void;
  // Whether the redesign is available at all. When false, hide every "try the new design" / switch
  // affordance — the kill switch has retired the beta.
  isBetaAvailable: boolean;
}>;

/**
 * Resolves which RSVP Responses page to render: the redesigned ("beta") page or the classic one.
 *
 * Two inputs combine: the `rsvpResponsesBetaEnabled` remote flag (the kill switch — defaults on) and
 * a per-admin localStorage preference (defaults to classic). When the kill switch is off, the result
 * is always 'classic' and `isBetaAvailable` is false, regardless of any stored preference.
 */
export const useRsvpResponsesVersion = (): UseRsvpResponsesVersionReturn => {
  const [isBetaAvailable] = useIsFeatureEnabled('rsvpResponsesBetaEnabled');
  // Local mirror of the shared module store. Subscribing on mount means opting into beta on one
  // surface immediately re-renders the others (page + tab-nav badge) without a remount. (React 17
  // has no `useSyncExternalStore`, so we wire the subscription by hand.)
  const [storedVersion, setStoredVersion] = useState<RsvpResponsesVersion>(getSnapshot);

  useEffect(() => {
    // Re-sync once on mount in case the store changed between the initial render and this effect.
    setStoredVersion(getSnapshot());
    return subscribe(() => setStoredVersion(getSnapshot()));
  }, []);

  const setVersion = useCallback((next: RsvpResponsesVersion) => {
    setVersionGlobal(next);
  }, []);

  const version: RsvpResponsesVersion = isBetaAvailable ? storedVersion : 'classic';

  return { version, setVersion, isBetaAvailable };
};
